import { ChangeEvent, useEffect, useState } from 'react';
import _ from 'lodash';

import { SpeechBubble } from '../../../../shared';

import { getNameSuggestionFromId } from '../../../utils';
import {
    DiscountType,
    DiscountTypes,
    generateSKUs,
    isValidFlatDiscount,
    isValidPercentageDiscount,
    PackWorthList,
    trimDecimalsTo,
} from './utils';

import { Pack, PriceLevel } from '../../../../types/packTypes';

import styles from './PackPriceIdGenerator.module.scss';

type PackPriceIdGeneratorProps = {
    pack: Pack;
    childPacks: PackWorthList;
    onSKUsGenerated: (priceLevels: PriceLevel[]) => void;
};

export function PackPriceIdGenerator(props: PackPriceIdGeneratorProps) {
    const { pack, childPacks, onSKUsGenerated } = props;
    const [totalWorth, setTotalWorth] = useState<number>(0);
    const [discount, setDiscount] = useState<number>(0);
    const [nameEdited, setNameEdited] = useState<boolean>(false);
    const [discountType, setDiscountType] = useState<DiscountType>(
        DiscountTypes.percentage,
    );
    const [name, setName] = useState<string>('');

    useEffect(() => {
        setTotalWorth(_.sumBy(childPacks, 'worth'));
    }, [childPacks]);

    useEffect(() => {
        if (!nameEdited) {
            setName(getNameSuggestionFromId(pack.packId));
        }
    }, [pack.packId]);

    function updateName(e: ChangeEvent<HTMLInputElement>) {
        setNameEdited(true);
        setName(e.target.value);
    }

    function updateDiscount(e: ChangeEvent<HTMLInputElement>) {
        if (e.target.value) {
            setDiscount(trimDecimalsTo(parseFloat(e.target.value), 1));
        } else {
            setDiscount(0);
        }
    }

    function updateDiscountType(e: ChangeEvent<HTMLInputElement>) {
        setDiscountType(e.target.value as DiscountType);
    }

    function getFinalDiscountedValue() {
        const previewValue =
            discountType == DiscountTypes.percentage
                ? totalWorth * (1 - discount / 100)
                : totalWorth - discount;
        return trimDecimalsTo(previewValue, 2);
    }

    function getDiscountError(value: number): string | null {
        if (
            discountType == DiscountTypes.percentage &&
            !isValidPercentageDiscount(value)
        ) {
            return 'This is not a valid percentage.';
        } else if (
            discountType == DiscountTypes.flat &&
            !isValidFlatDiscount(value, totalWorth)
        ) {
            return 'This is not a valid number.';
        }
        return null;
    }

    function onGenerateClicked() {
        onSKUsGenerated(
            generateSKUs({
                name,
                totalWorth,
                childPacks,
                discount,
                discountType,
                discountedTotal: getFinalDiscountedValue(),
            }),
        );
    }

    return (
        <>
            <h3>Generate SKUs for Bundle:</h3>
            <br />
            <div className="labelInput">
                <label htmlFor="name">Product ID/SKU</label>
                <span>com.tocaboca.tocalifeworld.</span>
                <input
                    className={`labelInput shortwide ${styles.nameInput}`}
                    type="text"
                    id="name"
                    value={name}
                    name="name"
                    onChange={updateName}
                />
            </div>
            <div className="labelInput">
                <label htmlFor="value">Bundle Value</label>
                <input
                    className="labelInput prices tiny"
                    type="text"
                    disabled={true}
                    id="value"
                    value={totalWorth}
                    name="value"
                />
            </div>
            <fieldset className={styles.radioFieldset}>
                <legend>Discount by</legend>

                <input
                    checked={discountType == DiscountTypes.percentage}
                    id="discount-percentage"
                    data-testid="discount-radio-percentage"
                    value={DiscountTypes.percentage}
                    type="radio"
                    onChange={updateDiscountType}
                />
                <label htmlFor="discount-percentage">Percentage</label>

                <input
                    checked={discountType == DiscountTypes.flat}
                    id="discount-flat"
                    data-testid="discount-radio-flat"
                    value={DiscountTypes.flat}
                    type="radio"
                    onChange={updateDiscountType}
                />
                <label htmlFor="discount-flat">Flat value</label>
            </fieldset>
            <div className="labelInput">
                <label htmlFor="discount">Discount</label>
                <input
                    className={`labelInput prices tiny`}
                    type="number"
                    id="discount"
                    value={discount.toString()}
                    name="discount"
                    onChange={updateDiscount}
                />
                {discountType == DiscountTypes.percentage && <span>%</span>}
            </div>
            <div className="labelInput">
                {getDiscountError(discount) ? (
                    <SpeechBubble>{getDiscountError(discount)}</SpeechBubble>
                ) : (
                    <>
                        <label>Preview:</label>
                        <span
                            data-testid="preview-value"
                            className={styles.displayValue}
                        >
                            = {getFinalDiscountedValue()}
                        </span>
                    </>
                )}
            </div>
            <br />
            <button
                onClick={onGenerateClicked}
                disabled={getDiscountError(discount) != null}
            >
                Generate SKUs
            </button>
            <hr className={styles.separator} />
        </>
    );
}
